<template>
  <div
    v-show="show"
    id="modal"
    :class="['wptv-modal-onei', theme]"
    tabindex="-1">
    <div
      class="modal-overlay"
      @click="clickOnOverlay" />
    <div
      ref="content"
      class="modal-content">
      <div
        v-if="feedback"
        class="modal-stamp">
        <span
          :class="['icon', icons[theme] ]"
          aria-hidden="true" />
      </div>
      <div
        v-if="closeIcon"
        class="modal-close">
        <button
          class="ob1-link-icon ob1-link-icon-small"
          aria-label="Fermer"
          @click="close">
          <span
            class="icon icon-delete" />
        </button>
      </div>
      <div class="modal-body">
        <div class="modal-title">
          <h1 class="no-signature m-0">
            <slot name="title" />
          </h1>
        </div>
        <div class="modal-infos">
          <slot name="content" />
        </div>
        <div
          v-if="$slots.hasOwnProperty('checkbox')"
          tabindex="1"
          class="modal-checkbox">
          <slot name="checkbox" />
        </div>
        <div
          v-if="$slots.hasOwnProperty('buttons')"
          ref="buttons"
          class="modal-buttons">
          <slot name="buttons" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const themes = ['info', 'service', 'warning', 'erreur', 'success']

export default {
  name: 'WptvModalOnei',
  props: {
    theme: {
      type: String,
      default: 'info',
      validator: (value) => themes.includes(value)
    },
    closeIcon: {
      type: Boolean,
      default: false
    },
    feedback: {
      type: Boolean,
      default: true
    },
    frozen: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      show: false,
      icons: {
        info: 'icon-info',
        warning: 'icon-Warning-important',
        erreur: 'icon-error-severe',
        success: 'icon-tick-circle'
      }
    }
  },
  methods: {
    close () {
      if (this.show) {
        this.show = false
        this.$refs.content.style.height = ''
        this.$emit('closed')
      }
    },

    open () {
      this.show = true
      this.$nextTick(() => {
        this.fixHeight()
        if (document.querySelector('#modal')) {
          // add all the elements inside modal which you want to make focusable
          const focusableElements =
            'button, a, checkbox, label, input, [tabindex]:not([tabindex="-1"])'
          const modal = document.querySelector('#modal') // select the modal by it's id

          const firstFocusableElement = modal.querySelectorAll(focusableElements)[0] // get first element to be focused inside modal
          const focusableContent = modal.querySelectorAll(focusableElements)
          const lastFocusableElement = focusableContent[focusableContent.length - 1] // get last element to be focused inside modal

          document.addEventListener('keydown', function (e) {
            const isTabPressed = e.key === 'Tab' || e.keyCode === 9

            if (!isTabPressed) {
              return
            }

            if (e.shiftKey) { // if shift key pressed for shift + tab combination
              if (document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus() // add focus for the last focusable element
                e.preventDefault()
              }
            } else { // if tab key is pressed
              if (document.activeElement === lastFocusableElement) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
                e.preventDefault()
                firstFocusableElement.focus() // add focus for the first focusable element
              }
            }
          })
          firstFocusableElement.focus()
        }
      })
    },

    clickOnOverlay () {
      if (!this.frozen) this.close()
    },

    isContentOverflow (element) {
      return element.scrollHeight > element.clientHeight
    },

    setHeigthFromMaxHeight (element) {
      const maxHeight = window.getComputedStyle(element)['max-height']
      if (maxHeight) {
        element.style.height = maxHeight
      }
    },

    fixHeight () {
      if (this.isContentOverflow(this.$refs.content)) { // IE11 ne gère pas bien le max-height sur du flex donc on set le height si besoin
        this.setHeigthFromMaxHeight(this.$refs.content)
      }
    }
  }
}

</script>

<style lang="scss" rel="stylesheet/scss" scoped>
//============================ IMPORTS
@import '../../scss/abstracts/variables';
@import '../../scss/abstracts/mixins';
@import '../../scss/abstracts/placeholders';

//============================ VARS (MEDIA_QUERIES)
$modal-padding: (
  xxsmall: 10px,
  medium: 30px
);
$modal-margin: (
  xxsmall: 30px
);

//============================ STATICS VARS
$gutter-width: 10px;

.wptv-modal-onei {
  height: 100%;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  display: block;
  z-index: 1000; //smart banner zindex is 999

  .modal-overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.7);
  }

  .modal-content {
    font-family: $main-font-family;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    @include mediaQueries('flex-direction', (medium: row));
    box-shadow: 5px 5px 5px 0 #333;
    background: $white;
    @include mediaQueries('max-width', (medium: 720px));
    width: 100%;
    @include mediaQueries('width', (medium: 75%, xlarge: 70%));
    max-height: 100%;
    @include mediaQueries('max-height', (medium: 75%, xlarge: 70%));
    height: 100%;
    @include mediaQueries('height', (medium: auto));

    .modal-stamp {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      color: $white;
      height: 135px;
      width: 100%;
      font-size: 75px;
      @include mediaQueries('height', (medium: auto));
      @include mediaQueries('max-width', (medium: 180px));
    }

    .modal-close {
      text-align: right;
      height: 60px;
      top: 0;
      right: 0;
      position: absolute;
      z-index: 10;
      border: none;
      background: none;

      button  {
        display: block;
        padding: 15px; // to have a sufficient touch surface on mobiles

        &:hover {
          cursor: pointer;
          color: $primary;
          .close-cross {
            display: none;
          }

        }
        &:focus {
          outline-color: transparent;
        }
        &:focus-visible {
          outline: solid 2px $primary;
        }
      }
    }

    .modal-body {
      padding: 30px 30px 45px;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      flex-shrink: 1;
      flex-grow: 1;

      .modal-title {
        @extend %bold;

        h1 {
          font-size: 20px;
          @include mediaQueries('font-size', (medium: 22px, xlarge: 26px));
          line-height: 24px;
          @include mediaQueries('line-height', (medium: 26px, xlarge: 30px));
        }
      }

      .modal-infos {
        font-family: Helvetica, Arial, sans-serif;
        line-height: 1.5em;
        overflow-y: auto;
        overflow-x: hidden;
        padding-top: 15px;
      }

      .modal-checkbox {
        height: 20px;
        @include mediaQueries('margin-top', $modal-margin);
        @include mediaQueries('padding-right', $modal-padding);
        &:focus {
          input{
            outline-color: transparent;
          }
        }
        &:focus-visible {
          input{
            outline: solid 2px $primary;
          }
        }
      }

      .modal-buttons > div {
        display: flex !important;
        justify-content: center;
        @include mediaQueries('justify-content', (medium: left));
        flex-direction: column;
        @include mediaQueries('flex-direction', (large: row));
        padding-top: 10px;
        @include mediaQueries('padding-top', (medium: 30px));

        button {
          margin-top: 10px;
          margin-right: 15px;
          &:focus {
            outline-color: transparent;
          }
          &:focus-visible {
            outline: solid 2px $primary;
          }

        }
      }
    }
  }

  &.info {
    .modal-stamp {
      background-color: map-get($wptv-palette, 'lightinfo');
    }
  }

  &.warning {
    .modal-stamp {
      background-color: map-get($wptv-palette, 'lightwarning');
    }
  }

  &.erreur {
    .modal-stamp {
      background-color: map-get($wptv-palette, 'lightalert');
    }
  }

  &.success {
    .modal-stamp {
      background-color: map-get($wptv-palette, 'lightsuccess');
    }
  }
}

</style>
